'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, ClipboardList, FileBarChart2, Dumbbell, Users, TrendingUp, ChevronLeft, ChevronRight, HeartPulse,  } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'nav-dashboard', label: 'Athlete Dashboard', href: '/athlete-dashboard', icon: LayoutDashboard },
  { id: 'nav-wellness', label: 'Daily Wellness', href: '/daily-wellness', icon: HeartPulse },
  { id: 'nav-assessment', label: 'Assessment Entry', href: '/assessment-entry', icon: ClipboardList },
  { id: 'nav-profile-report', label: 'Explosive Profile', href: '/explosive-profile-report', icon: FileBarChart2 },
  { id: 'nav-program', label: 'Training Program', href: '/training-program-builder', icon: Dumbbell },
  { id: 'nav-coach', label: 'Coach Dashboard', href: '/coach-dashboard', icon: Users },
  { id: 'nav-progress', label: 'Progress & Trends', href: '/progress-trends', icon: TrendingUp },
];

interface SidebarProps {
  activePath: string;
}

export default function Sidebar({ activePath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`flex flex-col border-r transition-all duration-300 ease-in-out flex-shrink-0 ${collapsed ? 'w-16' : 'w-60'}`}
      style={{
        background: 'rgba(10,10,10,0.95)',
        borderColor: 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Logo */}
      <div
        className={`flex items-center border-b ${collapsed ? 'justify-center px-0 py-4' : 'gap-3 px-4 py-4'}`}
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-white text-base tracking-tight whitespace-nowrap">
            TheFacility
          </span>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p className="px-3 mb-2 text-xs font-medium uppercase tracking-widest text-zinc-600">
            Navigation
          </p>
        )}
        {NAV_ITEMS.map(item => {
          const isActive = activePath === item.href;
          const ItemIcon = item.icon;
          return (
            <Link
              key={item.id}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center rounded-lg transition-all duration-200 group relative ${
                collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
              }`}
              style={isActive ? {
                background: 'rgba(225,6,0,0.12)',
                color: '#E10600',
                border: '1px solid rgba(225,6,0,0.25)',
                boxShadow: '0 0 12px rgba(225,6,0,0.1)',
              } : {
                color: '#71717a',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                  (e.currentTarget as HTMLElement).style.color = '#ffffff';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#71717a';
                }
              }}
            >
              <ItemIcon size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
              )}
              {collapsed && (
                <div
                  className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs text-zinc-200 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50"
                  style={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {item.label}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="px-2 py-4 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`w-full flex items-center rounded-lg text-zinc-600 hover:text-zinc-300 transition-all duration-200 mt-2 ${collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2'}`}
          style={{ background: 'transparent' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}